<template>
  <div class="admin-dashboard">
    <!-- Toast 通知 -->
    <transition-group name="toast" tag="div" class="toast-container" aria-live="polite">
      <div
        v-for="t in toastList"
        :key="t.id"
        class="toast-item"
        :class="'toast--' + t.type"
        role="status"
      >
        <svg class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path v-if="t.type === 'success'" d="M20 6 9 17l-5-5" />
          <template v-else-if="t.type === 'error'">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </template>
          <template v-else>
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </template>
        </svg>
        <span class="toast-text">{{ t.message }}</span>
        <button class="toast-close" type="button" aria-label="关闭提示" @click="dismissToast(t.id)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>
    </transition-group>

    <!-- 确认对话框 -->
    <transition name="modal">
      <div v-if="confirmDialog.show" class="modal-overlay" @click.self="resolveConfirm(false)">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
          <div class="modal-icon" :class="'modal-icon--' + confirmDialog.type" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path v-if="confirmDialog.type === 'danger'" d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path v-if="confirmDialog.type === 'danger'" d="M12 9v4" />
              <path v-if="confirmDialog.type === 'danger'" d="M12 17h.01" />
              <template v-else>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </template>
            </svg>
          </div>
          <h3 id="confirm-title" class="modal-title">{{ confirmDialog.title }}</h3>
          <p class="modal-message">{{ confirmDialog.message }}</p>
          <div v-if="confirmDialog.requireText" class="modal-input-wrap">
            <label class="modal-input-label" for="confirm-input">请输入 <code>{{ confirmDialog.requireText }}</code> 以确认</label>
            <input
              id="confirm-input"
              ref="confirmInput"
              v-model="confirmDialog.inputValue"
              class="modal-input"
              :placeholder="'请输入 ' + confirmDialog.requireText"
              @keyup.enter="resolveConfirm(true)"
            >
          </div>
          <div class="modal-actions">
            <button ref="confirmCancelBtn" type="button" class="btn btn--ghost" @click="resolveConfirm(false)">{{ confirmDialog.cancelText }}</button>
            <button
              type="button"
              class="btn"
              :class="confirmDialog.type === 'danger' ? 'btn--danger' : 'btn--primary'"
              :disabled="!!(confirmDialog.requireText && confirmDialog.inputValue.trim() !== confirmDialog.requireText)"
              @click="resolveConfirm(true)"
            >{{ confirmDialog.confirmText }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 页面标题 -->
    <header class="page-header">
      <div class="page-header__main">
        <h1 class="page-title">支付后台管理</h1>
        <p class="page-desc">退款、充值、账户余额与服务器状态的统一运维控制台</p>
      </div>
      <span class="page-badge">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></svg>
        管理端
      </span>
    </header>

    <!-- 概览统计 -->
    <section class="stats-grid" aria-label="概览">
      <div class="stat-card">
        <span class="stat-icon stat-icon--blue"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="8" height="4" x="8" y="2" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">待处理业务</span>
          <span class="stat-value">{{ pendingTotal }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="8" x="2" y="2" rx="2" /><rect width="20" height="8" x="2" y="14" rx="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">在线服务器</span>
          <span class="stat-value">{{ onlineServers }}<span class="stat-sub"> / {{ serversList.length }}</span></span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--slate"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">数据表</span>
          <span class="stat-value">{{ availableTables.length - 1 }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-icon stat-icon--violet"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /></svg></span>
        <div class="stat-body">
          <span class="stat-label">退款记录</span>
          <span class="stat-value">{{ refundPagination.total }}</span>
        </div>
      </div>
    </section>

    <!-- 退款管理 -->
    <section class="card card--primary">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /></svg></span>
          <h2>退款管理</h2>
        </div>
        <p class="card-hint">查询订单并执行原路退款</p>
      </header>

      <!-- 退款操作表单 -->
      <div class="panel">
        <div class="form-grid">
          <div class="field">
            <label class="field__label" for="refund-phone">用户手机号</label>
            <input
              id="refund-phone"
              v-model="refundForm.phone"
              type="text"
              inputmode="numeric"
              class="input"
              placeholder="请输入用户手机号"
              maxlength="11"
              @blur="validatePhone"
            >
            <span v-if="phoneError" class="field__error">{{ phoneError }}</span>
          </div>
          <div class="field">
            <label class="field__label" for="refund-order">支付宝订单号</label>
            <input
              id="refund-order"
              v-model="refundForm.alipayOrderId"
              type="text"
              class="input"
              placeholder="请输入支付宝订单号"
              maxlength="64"
            >
          </div>
          <div class="field field--button">
            <button type="button" class="btn btn--primary" :disabled="querying || !canQueryOrder" @click="queryOrderInfo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
              {{ querying ? '查询中…' : '查询订单' }}
            </button>
          </div>
        </div>

        <!-- 订单信息 -->
        <div v-if="orderInfo" class="order-info">
          <div class="order-info__amounts">
            <div class="info-item">
              <span class="info-label">订单金额</span>
              <span class="info-value">{{ formatAmount(orderInfo.amount) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">已退金额</span>
              <span class="info-value info-value--danger">-{{ formatAmount(orderInfo.refunded) }}</span>
            </div>
            <div class="info-item info-item--accent">
              <span class="info-label">可退金额</span>
              <span class="info-value">{{ formatAmount(orderInfo.available) }}</span>
            </div>
          </div>
          <div class="order-info__meta">
            <div class="meta-item"><span class="info-label">订单时间</span><span class="meta-value">{{ orderInfo.create_time }}</span></div>
            <div class="meta-item"><span class="info-label">订单状态</span><span :class="['badge', 'badge--' + orderInfo.status.toLowerCase()]">{{ formatOrderStatus(orderInfo.status) }}</span></div>
            <div class="meta-item"><span class="info-label">支付方式</span><span class="meta-value">{{ orderInfo.payment_method || '支付宝' }}</span></div>
          </div>
        </div>

        <!-- 退款金额与原因 -->
        <div v-if="orderInfo && orderInfo.status === 'PAID'" class="form-grid form-grid--refund">
          <div class="field">
            <label class="field__label" for="refund-amount">退款金额（元）</label>
            <div class="amount-input-wrap">
              <span class="amount-prefix">¥</span>
              <input
                id="refund-amount"
                v-model.number="refundForm.amount"
                type="number"
                class="input input--amount"
                min="0.01"
                step="0.01"
                :max="orderInfo.available"
                placeholder="0.00"
                @input="validateRefundAmount"
              >
            </div>
            <span v-if="amountError" class="field__error">{{ amountError }}</span>
            <span v-else class="field__hint">最多可退 ¥{{ formatAmount(orderInfo.available) }}</span>
          </div>
          <div class="field">
            <label class="field__label" for="refund-reason">退款原因（选填）</label>
            <input
              id="refund-reason"
              v-model="refundForm.reason"
              type="text"
              class="input"
              placeholder="用户不满意 / 系统问题等"
              maxlength="100"
            >
          </div>
          <div class="field field--button">
            <button type="button" class="btn btn--amber" :disabled="submitting || !canRefund" @click="submitRefund">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              {{ submitting ? '处理中…' : '确认退款' }}
            </button>
          </div>
        </div>

        <div v-if="orderInfo && orderInfo.status !== 'PAID'" class="alert alert--warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
          当前订单状态为「{{ formatOrderStatus(orderInfo.status) }}」，仅已支付订单可操作退款。
        </div>

        <!-- 退款结果 -->
        <div v-if="refundResult" class="result-msg" :class="'result-msg--' + refundResult.type" role="status" aria-live="polite">
          <div class="result-msg__head">
            <strong>{{ refundResult.title }}</strong>
            <button v-if="refundResult.type === 'success'" type="button" class="result-msg__close" aria-label="关闭" @click="refundResult = null">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </div>
          <p>{{ refundResult.message }}</p>
          <div v-if="refundResult.data" class="result-msg__detail">
            <p>退款请求号：{{ refundResult.data.out_request_no }}</p>
            <p>退款类型：{{ refundResult.data.refund_type }}</p>
            <p>用户新余额：¥{{ formatAmount(refundResult.data.user_balance) }}</p>
            <p v-if="refundResult.data.alipay_trade_no">支付宝交易号：{{ refundResult.data.alipay_trade_no }}</p>
          </div>
        </div>
      </div>

      <!-- 退款记录 -->
      <div class="sub-section">
        <div class="sub-header">
          <div class="card-title">
            <span class="card-icon card-icon--sm"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></svg></span>
            <h3>退款记录</h3>
          </div>
          <div class="filter-bar">
            <input v-model="recordFilter.phone" type="text" inputmode="numeric" class="input input--sm" placeholder="手机号筛选" maxlength="11">
            <select v-model="recordFilter.status" class="input input--sm">
              <option value="">全部状态</option>
              <option value="SUCCESS">成功</option>
              <option value="FAILED">失败</option>
              <option value="PROCESSING">处理中</option>
            </select>
            <button type="button" class="btn btn--ghost btn--sm" @click="loadRefundRecords(1)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
              筛选
            </button>
            <button type="button" class="btn btn--ghost btn--sm" @click="refreshRecords">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
              刷新
            </button>
          </div>
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>退款时间</th>
                <th>用户手机号</th>
                <th>支付宝订单号</th>
                <th>退款金额</th>
                <th>原因</th>
                <th>状态</th>
                <th>请求号</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in refundRecords" :key="item.id">
                <td class="td-nowrap">{{ item.create_time }}</td>
                <td>{{ item.user_phone }}</td>
                <td class="td-mono td-truncate" :title="item.alipay_order_id">{{ item.alipay_order_id }}</td>
                <td class="td-amount">-¥{{ formatAmount(item.refund_amount) }}</td>
                <td class="td-truncate" :title="item.refund_reason">{{ item.refund_reason || '-' }}</td>
                <td><span :class="['badge', 'badge--' + item.status.toLowerCase()]">{{ formatRefundStatus(item.status) }}</span></td>
                <td class="td-mono td-truncate" :title="item.out_request_no">{{ item.out_request_no }}</td>
              </tr>
              <tr v-if="refundRecords.length === 0">
                <td colspan="7" class="empty-tip">{{ loadingRecords ? '加载中…' : '暂无退款记录' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="refundPagination.total > 0" class="pagination">
          <button type="button" class="btn btn--ghost btn--sm" :disabled="refundPagination.page <= 1" @click="changePage(refundPagination.page - 1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
            上一页
          </button>
          <span class="pagination__info">第 {{ refundPagination.page }} / {{ refundPagination.total_pages }} 页 · 共 {{ refundPagination.total }} 条</span>
          <button type="button" class="btn btn--ghost btn--sm" :disabled="refundPagination.page >= refundPagination.total_pages" @click="changePage(refundPagination.page + 1)">
            下一页
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
          </button>
        </div>
      </div>
    </section>

    <!-- 用户表查询 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg></span>
          <h2>用户表信息查询</h2>
        </div>
        <p class="card-hint">按手机号检索用户与账户余额</p>
      </header>
      <div class="filter-row">
        <div class="field field--inline">
          <label class="field__label" for="user-phone-filter">手机号</label>
          <input id="user-phone-filter" v-model="userQueryPhone" type="text" inputmode="numeric" class="input" placeholder="请输入手机号（可选）" maxlength="11">
        </div>
        <button type="button" class="btn btn--primary" @click="queryUsersTable">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          查询用户
        </button>
      </div>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>序号</th><th>用户手机号</th><th>账户余额</th></tr>
          </thead>
          <tbody>
            <tr v-for="(user, index) in userList" :key="user.user_phone">
              <td class="td-muted">{{ index + 1 }}</td>
              <td>{{ user.user_phone }}</td>
              <td class="td-amount td-amount--positive">¥{{ formatAmount(user.account_balance) }}</td>
            </tr>
            <tr v-if="userList.length === 0">
              <td colspan="3" class="empty-tip">{{ userQueryPhone ? '未找到匹配用户' : '暂无用户数据' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 充值记录 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></svg></span>
          <h2>用户充值记录查询</h2>
        </div>
        <p class="card-hint">按手机号、日期与支付状态筛选</p>
      </header>
      <div class="filter-row filter-row--wrap">
        <div class="field field--inline">
          <label class="field__label" for="phone-filter">手机号</label>
          <input id="phone-filter" v-model="filterPhone" type="text" inputmode="numeric" class="input" placeholder="请输入手机号" maxlength="11">
        </div>
        <div class="field field--inline">
          <label class="field__label" for="date-filter">日期</label>
          <input id="date-filter" v-model="filterDate" type="date" class="input">
        </div>
        <div class="field field--inline">
          <label class="field__label" for="status-filter">支付状态</label>
          <select id="status-filter" v-model="filterStatus" class="input">
            <option value="">全部</option>
            <option value="PAID">成功</option>
            <option value="FAILED">失败</option>
            <option value="PENDING">待支付</option>
          </select>
        </div>
        <button type="button" class="btn btn--primary" @click="queryRecords">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          查询
        </button>
      </div>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>订单ID</th><th>用户手机号</th><th>充值金额</th><th>订单状态</th><th>创建时间</th><th>支付时间</th><th>支付方式</th><th>支付宝订单号</th></tr>
          </thead>
          <tbody>
            <tr v-for="record in rechargeRecords" :key="record.id">
              <td class="td-muted">{{ record.id }}</td>
              <td>{{ record.user_phone }}</td>
              <td class="td-amount td-amount--positive">¥{{ formatAmount(record.amount) }}</td>
              <td><span :class="['badge', 'badge--' + record.status.toLowerCase()]">{{ formatStatus(record.status) }}</span></td>
              <td class="td-nowrap">{{ formatDateTime(record.create_time) }}</td>
              <td class="td-nowrap">{{ record.pay_time ? formatDateTime(record.pay_time) : '-' }}</td>
              <td>{{ record.payment_method || '支付宝' }}</td>
              <td class="td-mono td-truncate" :title="record.alipay_order_id">{{ record.alipay_order_id || '-' }}</td>
            </tr>
            <tr v-if="rechargeRecords.length === 0">
              <td colspan="8" class="empty-tip">暂无充值记录</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 账户金额管理 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg></span>
          <h2>用户账户金额管理</h2>
        </div>
        <p class="card-hint">查询余额并手动调整账户金额</p>
      </header>
      <div class="account-controls">
        <div class="account-row">
          <div class="field field--inline">
            <label class="field__label" for="account-phone">手机号</label>
            <input id="account-phone" v-model="accountPhone" type="text" inputmode="numeric" class="input" placeholder="请输入手机号" maxlength="11">
          </div>
          <button type="button" class="btn btn--primary" @click="queryAccountBalance">查询余额</button>
          <div class="balance-panel">
            <span class="balance-panel__label">当前余额</span>
            <span class="balance-panel__value">¥{{ currentBalance !== null ? formatAmount(currentBalance) : '0.00' }}</span>
          </div>
        </div>
        <div class="account-row">
          <div class="field field--inline">
            <label class="field__label" for="amount-change">金额变更</label>
            <input id="amount-change" v-model.number="amountChange" type="number" step="0.01" class="input" placeholder="正数增加，负数减少">
          </div>
          <button type="button" class="btn btn--amber" @click="changeAccountBalance">修改金额</button>
        </div>
      </div>
    </section>

    <!-- 未处理业务 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="8" height="4" x="8" y="2" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" /></svg></span>
          <h2>未处理业务总量</h2>
        </div>
        <button type="button" class="btn btn--ghost btn--sm" @click="refreshPendingTasks">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
          刷新
        </button>
      </header>
      <div class="task-grid">
        <div v-for="(count, type) in pendingTasks" :key="type" class="task-chip" :class="{ 'task-chip--empty': !count }">
          <span class="task-chip__name">{{ formatTaskType(type) }}</span>
          <span class="task-chip__count">{{ count }}</span>
        </div>
      </div>
    </section>

    <!-- 服务器状态 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="8" x="2" y="2" rx="2" /><rect width="20" height="8" x="2" y="14" rx="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" /></svg></span>
          <h2>服务器状态监控</h2>
        </div>
        <button type="button" class="btn btn--ghost btn--sm" @click="refreshServersStatus">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
          刷新
        </button>
      </header>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>服务器ID</th><th>IP地址</th><th>端口</th><th>服务类型</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            <tr v-for="server in serversList" :key="server.server_id">
              <td class="td-mono">{{ server.server_id }}</td>
              <td class="td-mono">{{ server.ip_address }}</td>
              <td class="td-muted">{{ server.port }}</td>
              <td>{{ formatServiceType(server.service_type) }}</td>
              <td><span :class="['badge', 'badge--' + server.status]">{{ formatServerStatus(server.status) }}</span></td>
              <td>
                <button type="button" class="btn btn--danger btn--sm" @click="handleDeleteServer(server.server_id)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                  删除
                </button>
              </td>
            </tr>
            <tr v-if="serversList.length === 0">
              <td colspan="6" class="empty-tip">暂无服务器数据</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 备份与恢复 -->
    <section class="card">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" /></svg></span>
          <h2>数据表备份与恢复</h2>
        </div>
        <p class="card-hint">支持单表与全表批量操作</p>
      </header>

      <div v-if="showBackupList" class="sub-section sub-section--flush">
        <div class="sub-header">
          <h3 class="sub-title">已有备份文件</h3>
          <button type="button" class="btn btn--ghost btn--sm" @click="loadBackupList">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
            刷新列表
          </button>
        </div>
        <div class="table-wrap table-wrap--small">
          <table class="table">
            <thead>
              <tr><th>数据表</th><th>文件名</th><th>大小</th><th>修改时间</th><th>操作</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in backupFileList" :key="item.table_name">
                <td>{{ item.table_name }}</td>
                <td class="td-mono td-truncate" :title="item.file_name">{{ item.file_name }}</td>
                <td class="td-muted">{{ item.size_kb }} KB</td>
                <td class="td-nowrap">{{ formatDateTime(item.modified) }}</td>
                <td>
                  <button type="button" class="btn btn--ghost btn--sm" :disabled="loading" @click="quickRestore(item.table_name)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                    恢复
                  </button>
                </td>
              </tr>
              <tr v-if="backupFileList.length === 0">
                <td colspan="5" class="empty-tip">{{ loadingBackupList ? '加载中…' : '暂无备份文件' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="backup-controls">
        <div class="field field--inline">
          <label class="field__label" for="table-select">选择数据表</label>
          <select id="table-select" v-model="selectedTable" class="input">
            <option value="">请选择需要操作的表</option>
            <option v-for="tbl in availableTables" :key="tbl" :value="tbl">{{ tbl === 'all' ? '全部数据表（批量操作）' : tbl }}</option>
          </select>
        </div>
        <button type="button" class="btn btn--success" :disabled="!selectedTable || loading" @click="handleBackup">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
          {{ loading && actionType === 'backup' ? '备份中…' : '备份数据表' }}
        </button>
        <button type="button" class="btn btn--danger" :disabled="!selectedTable || loading" @click="handleRestore">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
          {{ loading && actionType === 'restore' ? '恢复中…' : '恢复数据表' }}
        </button>
      </div>

      <div v-if="backupMsg" class="result-msg" :class="'result-msg--' + backupType" role="status" aria-live="polite">
        {{ backupMsg }}
        <div v-if="backupDetails" class="result-msg__detail">
          详情：成功 {{ backupDetails.success?.length || 0 }} / 失败 {{ backupDetails.failed?.length || 0 }}
          <div v-if="backupDetails.failed?.length > 0">失败：{{ backupDetails.failed.map(f => f.table).join(', ') }}</div>
        </div>
      </div>

      <div v-if="selectedTable === 'all' && actionType === 'restore'" class="alert alert--danger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
        高危操作警告：全表恢复将清空并覆盖所有 {{ availableTables.length - 1 }} 个数据表的现有数据，此操作不可逆！请谨慎确认。
      </div>
    </section>

    <!-- 清理过期文件 -->
    <section class="card card--danger">
      <header class="card-header">
        <div class="card-title">
          <span class="card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg></span>
          <h2>清除过期文件</h2>
        </div>
        <p class="card-hint">清理超过 30 天的记录与七牛云文件</p>
      </header>
      <div class="cleanup-info">
        <p>功能说明：清理所有业务表中上传时间超过 <strong>30 天</strong> 的过期记录，并同步删除七牛云存储中的对应文件。</p>
        <p class="cleanup-info__warn">此操作不可逆，请谨慎执行！</p>
      </div>
      <button type="button" class="btn btn--danger" :disabled="cleanupLoading" @click="handleCleanupExpiredRecords">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
        {{ cleanupLoading ? '清理中…' : '清理过期文件' }}
      </button>
      <div v-if="cleanupResult" class="result-msg" :class="'result-msg--' + cleanupResult.type" role="status" aria-live="polite">
        <strong>{{ cleanupResult.message }}</strong>
        <div v-if="cleanupResult.data" class="result-msg__detail">
          <p>已扫描业务表：{{ cleanupResult.data.tables_scanned }}</p>
          <p>已删除记录：{{ cleanupResult.data.records_deleted }}</p>
          <p>已删除七牛文件：{{ cleanupResult.data.qiniu_deleted }}</p>
          <p v-if="cleanupResult.data.qiniu_failed">七牛文件删除失败：{{ cleanupResult.data.qiniu_failed }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// 原有 API
import {
  getRechargeRecords,
  getAccountBalance,
  updateAccountBalance,
  getPendingTasks,
  getServersStatus,
  deleteServer,
  saveTableData,
  restoreTableData,
  queryUsersTable,
  // 【新增】退款相关 API
  adminRefund,
  queryOrderInfo,
  getRefundRecords,
  // 【新增】备份列表 API
  getBackupList,
  // 【新增】清除过期记录接口
  cleanupExpiredRecords
} from '@/api/upfile'

export default {
  name: 'AdminDashboard',

  data () {
    return {
      // ==================== 退款管理 ====================
      refundForm: {
        phone: '',
        alipayOrderId: '',
        amount: null,
        reason: ''
      },
      phoneError: '',
      amountError: '',
      querying: false,
      submitting: false,
      orderInfo: null,
      refundResult: null,

      recordFilter: {
        phone: '',
        status: ''
      },
      refundRecords: [],
      loadingRecords: false,
      refundPagination: {
        page: 1,
        page_size: 20,
        total: 0,
        total_pages: 0
      },

      // ==================== 清除过期文件 ====================
      cleanupLoading: false,
      cleanupResult: null,

      // ==================== 原有数据 ====================
      userQueryPhone: '',
      userList: [],

      rechargeRecords: [],
      filterPhone: '',
      filterDate: '',
      filterStatus: '',

      accountPhone: '',
      currentBalance: null,
      amountChange: 0,

      pendingTasks: {
        videoDownload: 0,
        audioSubtitle: 0,
        audioVoiceSeparation: 0,
        oneclickVideoTranslation: 0,
        imgConvert: 0,
        imgMatting: 0,
        imgUpscaleSR: 0,
        videoConvert: 0,
        videoSubtitle: 0,
        videoUpscaleSR: 0,
        videoMatting: 0
      },

      serversList: [],

      selectedTable: '',
      backupMsg: '',
      backupType: 'info',
      backupDetails: null,
      loading: false,
      actionType: '',
      availableTables: [
        'all',
        'users', 'orders', 'servers', 'refunds',
        'videoDownload', 'audioSubtitle', 'audioVoiceSeparation',
        'imgConvert', 'imgMatting', 'imgUpscaleSR',
        'videoConvert', 'videoSubtitle', 'videoUpscaleSR', 'videoMatting',
        'oneclickVideoTranslation'
      ],

      showBackupList: true,
      backupFileList: [],
      loadingBackupList: false,

      // ==================== 交互反馈 ====================
      toastList: [],
      toastId: 0,
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: '确定',
        cancelText: '取消',
        requireText: '',
        inputValue: '',
        resolve: null
      }
    }
  },

  computed: {
    canQueryOrder () {
      const phone = this.refundForm.phone.trim()
      const orderId = this.refundForm.alipayOrderId.trim()
      return /^1[3-9]\d{9}$/.test(phone) && orderId.length > 0
    },

    canRefund () {
      return this.orderInfo &&
             this.orderInfo.status === 'PAID' &&
             this.refundForm.amount > 0.009 &&
             this.refundForm.amount <= this.orderInfo.available + 0.001
    },

    pendingTotal () {
      return Object.values(this.pendingTasks).reduce((sum, n) => sum + (Number(n) || 0), 0)
    },

    onlineServers () {
      return this.serversList.filter(s => s.status === 'available').length
    }
  },

  created () {
    this.queryRecords()
    this.loadPendingTasks()
    this.loadServersStatus()
    this.loadRefundRecords()
    this.loadBackupList()
  },

  mounted () {
    window.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    // ==================== 退款管理 ====================
    validatePhone () {
      const phone = this.refundForm.phone.trim()
      if (phone && !/^1[3-9]\d{9}$/.test(phone)) {
        this.phoneError = '请输入 11 位有效手机号'
        return false
      }
      this.phoneError = ''
      return true
    },

    validateRefundAmount () {
      const amount = this.refundForm.amount
      if (!amount || amount <= 0) {
        this.amountError = '退款金额必须大于 0'
        return false
      }
      if (this.orderInfo && amount > this.orderInfo.available + 0.001) {
        this.amountError = `最多可退 ¥${this.formatAmount(this.orderInfo.available)}`
        return false
      }
      this.amountError = ''
      return true
    },

    async queryOrderInfo () {
      if (!this.validatePhone()) {
        this.showMessage('warning', '请输入正确的手机号')
        return
      }

      const alipayOrderId = this.refundForm.alipayOrderId.trim()
      if (!alipayOrderId) {
        this.showMessage('warning', '请输入支付宝订单号')
        return
      }

      this.querying = true
      this.orderInfo = null
      this.refundResult = null
      this.amountError = ''

      try {
        const res = await queryOrderInfo({
          phone: this.refundForm.phone.trim(),
          alipay_order_id: alipayOrderId
        })

        if (res.status === 200) {
          this.orderInfo = res.data
          this.refundForm.amount = parseFloat(this.orderInfo.available.toFixed(2))
          this.showMessage('success', '订单查询成功')
        } else {
          this.showMessage('error', res.message || '查询失败')
          this.orderInfo = null
        }
      } catch (error) {
        console.error('查询订单异常:', error)
        this.showMessage('error', '网络请求失败，请检查连接')
        this.orderInfo = null
      } finally {
        this.querying = false
      }
    },

    async submitRefund () {
      if (!this.canRefund) {
        this.showMessage('warning', '请输入有效的退款金额')
        return
      }

      const confirmed = await this.confirmAction({
        title: '确认退款',
        type: 'danger',
        confirmText: '确认退款',
        message: `用户：${this.refundForm.phone}\n订单号：${this.refundForm.alipayOrderId}\n退款金额：¥${this.refundForm.amount.toFixed(2)}\n原因：${this.refundForm.reason || '无'}\n\n此操作将原路退回支付宝，且不可撤销。`
      })
      if (!confirmed) return

      this.submitting = true
      this.refundResult = null

      try {
        const res = await adminRefund({
          user_phone: this.refundForm.phone.trim(),
          alipay_order_id: this.refundForm.alipayOrderId.trim(),
          refund_amount: parseFloat(this.refundForm.amount.toFixed(2)),
          refund_reason: this.refundForm.reason?.trim() || '管理员手动退款'
        })

        if (res.status === 200) {
          this.refundResult = {
            type: 'success',
            title: '退款成功',
            message: '退款申请已提交，资金将原路退回用户支付宝账户，1-5 个工作日到账。',
            data: res.data
          }
          this.showMessage('success', '退款成功')
          this.queryOrderInfo()
          this.loadRefundRecords(1)
          this.refundForm.amount = null
          this.refundForm.reason = ''
        } else {
          this.refundResult = {
            type: 'error',
            title: '退款失败',
            message: res.message || '支付宝处理失败，请稍后重试'
          }
          this.showMessage('error', res.message || '退款失败')
        }
      } catch (error) {
        console.error('退款请求异常:', error)
        this.refundResult = {
          type: 'error',
          title: '请求异常',
          message: '网络请求失败，请检查连接后重试'
        }
        this.showMessage('error', '退款请求失败')
      } finally {
        this.submitting = false
      }
    },

    async loadRefundRecords (page = 1) {
      this.loadingRecords = true
      try {
        const params = {
          page,
          page_size: this.refundPagination.page_size,
          phone: this.recordFilter.phone.trim() || undefined,
          status: this.recordFilter.status || undefined
        }
        const res = await getRefundRecords(params)
        if (res.status === 200) {
          this.refundRecords = res.data.list
          this.refundPagination = res.data.pagination
        } else {
          this.showMessage('error', '加载退款记录失败')
        }
      } catch (error) {
        console.error('加载退款记录异常:', error)
        this.showMessage('error', '网络请求失败')
      } finally {
        this.loadingRecords = false
      }
    },

    refreshRecords () {
      this.loadRefundRecords(this.refundPagination.page)
      this.showMessage('info', '已刷新')
    },

    changePage (page) {
      if (page < 1 || page > this.refundPagination.total_pages) return
      this.loadRefundRecords(page)
    },

    formatRefundStatus (status) {
      const map = { SUCCESS: '成功', FAILED: '失败', PROCESSING: '处理中' }
      return map[status] || status
    },

    formatOrderStatus (status) {
      const map = { PAID: '已支付', PENDING: '待支付', FAILED: '已失败' }
      return map[status] || status
    },

    // ==================== 清除过期文件 ====================
    async handleCleanupExpiredRecords () {
      const confirmed = await this.confirmAction({
        title: '清理过期文件',
        type: 'danger',
        confirmText: '确认清理',
        message: '确定要清理所有超过 30 天的过期任务记录及对应的七牛云文件吗？\n\n此操作将：\n1. 扫描所有业务表中上传时间超过 30 天的记录\n2. 批量删除对应的七牛云存储文件\n3. 删除数据库中的过期记录\n4. 操作不可逆，数据无法找回！'
      })
      if (!confirmed) return

      this.cleanupLoading = true
      this.cleanupResult = null

      try {
        const res = await cleanupExpiredRecords()

        if (res.status === 200) {
          this.cleanupResult = {
            type: 'success',
            message: '清理完成',
            data: res.data
          }
          this.showMessage('success', '过期数据清理完成')
        } else {
          this.cleanupResult = {
            type: 'error',
            message: res.message || '清理失败'
          }
          this.showMessage('error', res.message || '清理失败')
        }
      } catch (error) {
        console.error('清理过期记录异常:', error)
        this.cleanupResult = {
          type: 'error',
          message: '网络请求失败，请检查连接'
        }
        this.showMessage('error', '清理请求失败')
      } finally {
        this.cleanupLoading = false
      }
    },

    // ==================== 备份文件列表 ====================
    async loadBackupList () {
      this.loadingBackupList = true
      try {
        const res = await getBackupList()
        if (res.status === 200) {
          this.backupFileList = res.data
        }
      } catch (e) {
        console.warn('加载备份列表失败:', e)
      } finally {
        this.loadingBackupList = false
      }
    },

    /** 快速恢复单个表（从备份列表） */
    quickRestore (tableName) {
      this.selectedTable = tableName
      this.handleRestore()
    },

    // ==================== 备份与恢复 ====================
    async handleBackup () {
      if (!this.selectedTable) {
        this.showMessage('warning', '请先选择数据表')
        return
      }

      if (this.selectedTable === 'all') {
        const tableCount = this.availableTables.length - 1
        const confirmed = await this.confirmAction({
          title: '批量备份',
          type: 'warning',
          confirmText: '确认备份',
          message: `确定要备份所有 ${tableCount} 个数据表吗？\n此操作可能需要较长时间，期间请勿关闭页面。`
        })
        if (!confirmed) return
      }

      this.loading = true
      this.actionType = 'backup'
      this.backupDetails = null

      if (this.selectedTable === 'all') {
        this.backupMsg = '正在批量备份所有数据表，请稍候（可能需要较长时间）…'
      } else {
        this.backupMsg = `正在导出数据表 [${this.selectedTable}] 并保存至服务器…`
      }
      this.backupType = 'info'

      try {
        const res = await saveTableData({ table_name: this.selectedTable })

        if (res.status === 200) {
          if (this.selectedTable === 'all') {
            const details = res.details || {}
            const successCount = details.success?.length || 0
            const failedCount = details.failed?.length || 0
            const total = this.availableTables.length - 1

            this.backupMsg = `批量备份完成：\n成功: ${successCount}/${total} 个表`
            if (failedCount > 0) {
              this.backupMsg += `\n失败: ${failedCount} 个表`
              this.backupMsg += `\n失败详情: ${details.failed?.map(f => f.table).join(', ') || '未知'}`
            }
            this.backupMsg += '\n文件已保存至服务器备份目录'
          } else {
            this.backupMsg = `备份成功！文件已保存为 ${this.selectedTable}.csv`
          }
          this.backupType = 'success'
          this.showMessage('success', this.selectedTable === 'all' ? '批量备份完成' : '备份成功')
          this.loadBackupList()
        } else {
          if (this.selectedTable === 'all' && res.details) {
            const failedTables = res.details.failed?.map(f => `${f.table}: ${f.error}`).join('\n') || '未知错误'
            this.backupMsg = `批量备份部分失败:\n${failedTables}`
          } else {
            this.backupMsg = `备份失败: ${res.message}`
          }
          this.backupType = 'error'
          this.showMessage('error', res.message || '备份失败')
        }
        this.backupDetails = res.details || null
      } catch (e) {
        this.backupMsg = '网络请求失败'
        this.backupType = 'error'
        this.showMessage('error', '备份请求失败')
        console.error('Backup error:', e)
      } finally {
        this.loading = false
      }
    },

    async handleRestore () {
      if (!this.selectedTable) {
        this.showMessage('warning', '请先选择数据表')
        return
      }

      if (this.selectedTable === 'all') {
        const tableCount = this.availableTables.length - 1
        const confirmed = await this.confirmAction({
          title: '全表恢复（高危操作）',
          type: 'danger',
          confirmText: '确认恢复',
          requireText: 'CONFIRM_ALL',
          message: `确定要从备份文件恢复所有 ${tableCount} 个数据表吗？\n\n此操作将：\n1. 清空所有目标表的现有数据\n2. 用备份文件内容完全覆盖\n3. 操作不可逆，数据无法找回！\n\n请输入 CONFIRM_ALL 以确认继续。`
        })
        if (!confirmed) {
          this.showMessage('warning', '全表恢复已取消')
          return
        }
      } else {
        const confirmed = await this.confirmAction({
          title: '恢复数据表',
          type: 'danger',
          confirmText: '确认恢复',
          message: `确定要从备份文件恢复 [${this.selectedTable}] 吗？\n\n此操作将：\n1. 清空该表现有数据\n2. 覆盖为备份内容\n3. 操作不可逆！`
        })
        if (!confirmed) return
      }

      this.loading = true
      this.actionType = 'restore'
      this.backupDetails = null

      if (this.selectedTable === 'all') {
        this.backupMsg = '正在批量恢复所有数据表，请稍候（可能需要较长时间）…'
      } else {
        this.backupMsg = `正在读取文件并恢复数据表 [${this.selectedTable}]…`
      }
      this.backupType = 'info'

      try {
        const res = await restoreTableData({ table_name: this.selectedTable })

        if (res.status === 200) {
          if (this.selectedTable === 'all') {
            const details = res.details || {}
            const successCount = details.success?.length || 0
            const failedCount = details.failed?.length || 0
            const total = this.availableTables.length - 1

            this.backupMsg = `批量恢复完成：\n成功: ${successCount}/${total} 个表`
            if (failedCount > 0) {
              this.backupMsg += `\n失败: ${failedCount} 个表`
              this.backupMsg += `\n失败详情: ${details.failed?.map(f => f.table).join(', ') || '未知'}`
            }
          } else {
            this.backupMsg = `恢复成功！数据表 ${this.selectedTable} 已初始化完毕。`
          }
          this.backupType = 'success'
          this.showMessage('success', this.selectedTable === 'all' ? '批量恢复完成' : '恢复成功')
        } else {
          if (this.selectedTable === 'all' && res.details) {
            const failedTables = res.details.failed?.map(f => `${f.table}: ${f.error}`).join('\n') || '未知错误'
            this.backupMsg = `批量恢复部分失败:\n${failedTables}`
          } else {
            this.backupMsg = `恢复失败: ${res.message}`
          }
          this.backupType = 'error'
          this.showMessage('error', res.message || '恢复失败')
        }
        this.backupDetails = res.details || null
      } catch (e) {
        this.backupMsg = '网络请求失败'
        this.backupType = 'error'
        this.showMessage('error', '恢复请求失败')
        console.error('Restore error:', e)
      } finally {
        this.loading = false
      }
    },

    // ==================== 原有方法 ====================
    async queryUsersTable () {
      const phone = this.userQueryPhone.trim()
      if (phone && !this.validatePhoneFormat(phone)) {
        this.showMessage('warning', '请输入正确的手机号格式')
        return
      }
      try {
        const params = phone ? { phone } : {}
        const response = await queryUsersTable(params)
        if (response.status === 200) {
          this.userList = response.result
          this.showMessage('success', `查询成功，共 ${response.result.length} 条`)
        } else {
          this.showMessage('error', `查询失败: ${response.message}`)
          this.userList = []
        }
      } catch (error) {
        console.error(error)
        this.showMessage('error', '获取用户列表失败')
        this.userList = []
      }
    },

    async queryRecords () {
      const phone = this.filterPhone.trim()
      const date = this.filterDate
      const status = this.filterStatus

      if (phone && !this.validatePhoneFormat(phone)) {
        this.showMessage('warning', '请输入正确的手机号格式')
        return
      }

      const queryParams = {}
      if (phone) queryParams.phone = phone
      if (date) queryParams.date = date
      if (status) queryParams.status = status

      try {
        const response = await getRechargeRecords(queryParams)
        if (response.status === 200) {
          this.rechargeRecords = response.result
          this.showMessage('success', `查询成功，共 ${response.result.length} 条`)
        } else {
          this.showMessage('error', `查询失败: ${response.message}`)
        }
      } catch (error) {
        console.error(error)
        this.showMessage('error', '获取充值记录失败')
      }
    },

    async queryAccountBalance () {
      if (!this.accountPhone) {
        this.showMessage('warning', '请输入手机号')
        return
      }
      if (!this.validatePhoneFormat(this.accountPhone)) {
        this.showMessage('warning', '请输入正确的手机号格式')
        return
      }
      try {
        const response = await getAccountBalance({ phone: this.accountPhone })
        if (response.status === 200) {
          this.currentBalance = response.result.balance
          this.showMessage('success', '余额查询成功')
        } else {
          this.showMessage('error', `查询失败: ${response.message}`)
        }
      } catch (error) {
        console.error(error)
        this.showMessage('error', '获取账户余额失败')
      }
    },

    async changeAccountBalance () {
      if (!this.accountPhone) {
        this.showMessage('warning', '请输入手机号')
        return
      }
      if (!this.validatePhoneFormat(this.accountPhone)) {
        this.showMessage('warning', '请输入正确的手机号格式')
        return
      }
      if (isNaN(this.amountChange) || this.amountChange === 0) {
        this.showMessage('warning', '请输入有效的金额变更值')
        return
      }

      const action = this.amountChange > 0 ? '增加' : '减少'
      const confirmed = await this.confirmAction({
        title: '修改账户余额',
        type: 'warning',
        confirmText: '确认修改',
        message: `确认${action}用户 ${this.accountPhone} 余额 ¥${Math.abs(this.amountChange).toFixed(2)}？`
      })
      if (!confirmed) return

      try {
        const response = await updateAccountBalance({
          phone: this.accountPhone,
          amount: this.amountChange
        })
        if (response.status === 200) {
          this.showMessage('success', '账户金额修改成功')
          this.queryAccountBalance()
        } else {
          this.showMessage('error', `修改失败: ${response.message}`)
        }
      } catch (error) {
        console.error(error)
        this.showMessage('error', '修改账户金额失败')
      }
    },

    async loadPendingTasks () {
      try {
        const response = await getPendingTasks()
        if (response.status === 200) {
          this.pendingTasks = { ...this.pendingTasks, ...response.result }
        } else {
          console.warn('加载任务失败:', response.message)
        }
      } catch (error) {
        console.error(error)
      }
    },

    async refreshPendingTasks () {
      await this.loadPendingTasks()
      this.showMessage('info', '任务数据已刷新')
    },

    async loadServersStatus () {
      try {
        const response = await getServersStatus()
        if (response.status === 200) {
          this.serversList = response.result
        } else {
          console.warn('加载服务器状态失败:', response.message)
        }
      } catch (error) {
        console.error(error)
      }
    },

    async refreshServersStatus () {
      await this.loadServersStatus()
      this.showMessage('info', '服务器状态已刷新')
    },

    async handleDeleteServer (serverId) {
      const confirmed = await this.confirmAction({
        title: '删除服务器',
        type: 'danger',
        confirmText: '确认删除',
        message: '确定要删除该服务器记录吗？此操作不可恢复！'
      })
      if (!confirmed) return

      try {
        const response = await deleteServer({ server_id: serverId })
        if (response.status === 200) {
          this.showMessage('success', '删除成功')
          this.loadServersStatus()
        } else {
          this.showMessage('error', `删除失败: ${response.message}`)
        }
      } catch (error) {
        console.error(error)
        this.showMessage('error', '删除服务器记录失败')
      }
    },

    // ==================== 辅助方法 ====================
    validatePhoneFormat (phone) {
      return /^1[3-9]\d{9}$/.test(phone.replace(/\s+/g, ''))
    },

    formatAmount (amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    formatDateTime (str) {
      if (!str) return '-'
      try {
        if (typeof str === 'string' && str.includes('T')) {
          return str.replace('T', ' ').substring(0, 19)
        }
        return str.substring(0, 19)
      } catch {
        return str
      }
    },

    formatStatus (status) {
      const map = { PENDING: '待支付', PAID: '成功', FAILED: '失败' }
      return map[status] || status
    },

    formatServerStatus (status) {
      const map = { available: '可用', busy: '繁忙', offline: '离线' }
      return map[status] || status
    },

    formatServiceType (type) {
      const map = {
        video: '视频处理',
        audio: '音频处理',
        image: '图片处理',
        translate: '翻译服务',
        subtitle: '字幕服务'
      }
      return map[type] || type
    },

    formatTaskType (key) {
      const map = {
        videoDownload: '视频下载',
        audioSubtitle: '音频字幕',
        audioVoiceSeparation: '人声分离',
        oneclickVideoTranslation: '一键视频翻译',
        imgConvert: '图片转换',
        imgMatting: '图片抠图',
        imgUpscaleSR: '图片超分',
        videoConvert: '视频转换',
        videoSubtitle: '视频字幕',
        videoUpscaleSR: '视频超分',
        videoMatting: '视频抠图'
      }
      return map[key] || key
    },

    // ==================== 反馈系统 ====================
    showToast (type, message, duration = 3200) {
      const id = ++this.toastId
      this.toastList.push({ id, type, message })
      window.setTimeout(() => { this.dismissToast(id) }, duration)
    },

    dismissToast (id) {
      this.toastList = this.toastList.filter(t => t.id !== id)
    },

    showMessage (type, message) {
      const normalized = ['success', 'error', 'warning', 'info'].includes(type) ? type : 'info'
      this.showToast(normalized, message)
    },

    confirmAction (opts) {
      return new Promise((resolve) => {
        this.confirmDialog = {
          show: true,
          title: opts.title || '确认操作',
          message: opts.message || '',
          type: opts.type || 'warning',
          confirmText: opts.confirmText || '确定',
          cancelText: opts.cancelText || '取消',
          requireText: opts.requireText || '',
          inputValue: '',
          resolve
        }
        this.$nextTick(() => {
          if (opts.requireText) {
            const input = this.$refs.confirmInput
            if (input) input.focus()
          } else {
            const btn = this.$refs.confirmCancelBtn
            if (btn) btn.focus()
          }
        })
      })
    },

    resolveConfirm (accept) {
      const dialog = this.confirmDialog
      if (!dialog.show) return
      if (accept && dialog.requireText && dialog.inputValue.trim() !== dialog.requireText) {
        return
      }
      dialog.show = false
      const resolve = dialog.resolve
      dialog.resolve = null
      if (resolve) resolve(accept)
    },

    handleKeydown (e) {
      if (e.key === 'Escape' && this.confirmDialog.show) {
        this.resolveConfirm(false)
      }
    }
  }
}
</script>

<style lang="less" scoped>
// ==================== 设计令牌 ====================
@primary: #3555f4;
@primary-hover: #2a44d0;
@primary-soft: #eef1ff;
@success: #16a34a;
@success-soft: #ecfdf5;
@warning: #d97706;
@warning-soft: #fffbeb;
@danger: #dc2626;
@danger-soft: #fef2f2;
@info: #409eff;
@info-soft: #eff6ff;

@bg: #f5f7fb;
@surface: #ffffff;
@text: #1f2937;
@text-secondary: #64748b;
@text-muted: #94a3b8;
@border: #e5e7eb;
@border-strong: #d1d5db;

@radius: 12px;
@radius-sm: 8px;
@shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
@shadow-lg: 0 10px 30px rgba(15, 23, 42, 0.12);

@font: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;

.admin-dashboard {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 8px 4px 56px;
  font-family: @font;
  color: @text;
  font-size: 14px;
  line-height: 1.55;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

svg {
  width: 1em;
  height: 1em;
  flex-shrink: 0;
}

// ==================== 页面标题 ====================
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 4px 2px 20px;

  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: @text;
  }

  .page-desc {
    margin: 4px 0 0;
    font-size: 13px;
    color: @text-secondary;
  }

  .page-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 999px;
    background: @primary-soft;
    color: @primary;
    font-size: 12px;
    font-weight: 600;

    svg {
      width: 14px;
      height: 14px;
    }
  }
}

// ==================== 统计概览 ====================
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: @surface;
  border: 1px solid @border;
  border-radius: @radius;
  padding: 18px;
  box-shadow: @shadow;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  font-size: 20px;

  &--blue   { background: @primary-soft; color: @primary; }
  &--green  { background: @success-soft; color: @success; }
  &--slate  { background: #f1f5f9; color: @text-secondary; }
  &--violet { background: #f5f3ff; color: #7c3aed; }
}

.stat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: @text-secondary;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: @text;

  .stat-sub {
    font-size: 13px;
    font-weight: 600;
    color: @text-muted;
  }
}

// ==================== 卡片 ====================
.card {
  background: @surface;
  border: 1px solid @border;
  border-radius: @radius;
  padding: 22px 24px 24px;
  margin-bottom: 18px;
  box-shadow: @shadow;

  &--primary {
    border-color: #dfe6ff;
    box-shadow: 0 1px 3px rgba(53, 85, 244, 0.08), 0 8px 24px rgba(53, 85, 244, 0.06);
  }

  &--danger {
    border-color: #fecaca;
  }
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: @text;
  }

  .card-hint {
    margin: 0;
    font-size: 12px;
    color: @text-muted;
  }
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
}

.card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: @primary-soft;
  color: @primary;
  font-size: 17px;

  &--sm {
    width: 30px;
    height: 30px;
    font-size: 15px;
  }
}

// ==================== 面板 ====================
.panel {
  background: #fafbfe;
  border: 1px solid @border;
  border-radius: @radius-sm;
  padding: 18px;
}

.sub-section {
  margin-top: 20px;

  &--flush {
    margin-top: 12px;
  }
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  .sub-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }
}

// ==================== 表单 ====================
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 14px;
  align-items: start;

  &--refund {
    grid-template-columns: 1fr 1.4fr auto;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px dashed @border-strong;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  &--inline {
    flex-direction: row;
    align-items: center;
    gap: 10px;

    .field__label {
      white-space: nowrap;
      margin: 0;
    }
  }

  &--button {
    justify-content: flex-end;
    align-self: end;
  }

  .field__label {
    font-size: 13px;
    font-weight: 500;
    color: @text-secondary;
  }

  .field__error {
    font-size: 12px;
    color: @danger;
  }

  .field__hint {
    font-size: 12px;
    color: @text-muted;
  }
}

.input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  background: @surface;
  color: @text;
  border: 1px solid @border-strong;
  border-radius: @radius-sm;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: @text-muted;
  }

  &:hover {
    border-color: #a5b4fc;
  }

  &:focus {
    outline: none;
    border-color: @primary;
    box-shadow: 0 0 0 3px rgba(53, 85, 244, 0.16);
  }

  &--sm {
    height: 34px;
    font-size: 13px;
    width: auto;
  }

  &--amount {
    padding-left: 32px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    text-align: right;
  }
}

select.input {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;
  cursor: pointer;
}

.amount-input-wrap {
  position: relative;

  .amount-prefix {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: @text-secondary;
    font-weight: 600;
    pointer-events: none;
  }
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  &--wrap {
    align-items: center;
  }
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  .input--sm {
    width: 150px;
  }
}

// ==================== 按钮 ====================
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: @radius-sm;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.05s, box-shadow 0.15s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid @primary;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--primary {
    background: @primary;
    color: #fff;

    &:hover:not(:disabled) {
      background: @primary-hover;
      box-shadow: 0 4px 12px rgba(53, 85, 244, 0.24);
    }
  }

  &--success {
    background: @success;
    color: #fff;

    &:hover:not(:disabled) {
      background: #15803d;
      box-shadow: 0 4px 12px rgba(22, 163, 74, 0.24);
    }
  }

  &--danger {
    background: @danger;
    color: #fff;

    &:hover:not(:disabled) {
      background: #b91c1c;
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.24);
    }
  }

  &--amber {
    background: @warning;
    color: #fff;

    &:hover:not(:disabled) {
      background: #b45309;
      box-shadow: 0 4px 12px rgba(217, 119, 6, 0.24);
    }
  }

  &--ghost {
    background: @surface;
    color: @text-secondary;
    border-color: @border-strong;

    &:hover:not(:disabled) {
      color: @text;
      border-color: @text-muted;
      background: #f9fafb;
    }
  }

  &--sm {
    min-height: 34px;
    padding: 0 12px;
    font-size: 13px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
}

// ==================== 订单信息 ====================
.order-info {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 18px;
  margin-top: 18px;
  padding: 16px 18px;
  background: @surface;
  border: 1px solid @border;
  border-left: 3px solid @primary;
  border-radius: @radius-sm;

  &__amounts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 18px;
    border-left: 1px solid @border;

    .meta-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      .meta-value {
        font-weight: 500;
        color: @text;
      }
    }
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;

  &--accent {
    padding: 10px 12px;
    background: @primary-soft;
    border-radius: @radius-sm;

    .info-value {
      color: @primary;
    }
  }
}

.info-label {
  font-size: 12px;
  color: @text-secondary;
}

.info-value {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: @text;

  &--danger {
    color: @danger;
  }
}

// ==================== 状态徽章 ====================
.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.6;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &--paid,
  &--success,
  &--available {
    background: @success-soft;
    color: @success;
  }

  &--pending,
  &--processing,
  &--busy {
    background: @warning-soft;
    color: @warning;
  }

  &--failed,
  &--offline {
    background: @danger-soft;
    color: @danger;
  }
}

// ==================== 提示与结果 ====================
.alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: @radius-sm;
  font-size: 13px;
  margin-top: 14px;

  svg {
    width: 17px;
    height: 17px;
    margin-top: 1px;
  }

  &--warning {
    background: @warning-soft;
    color: #92400e;
    border: 1px solid #fde68a;
  }

  &--danger {
    background: @danger-soft;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
}

.result-msg {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: @radius-sm;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-line;

  strong {
    display: block;
    font-size: 14px;
    margin-bottom: 4px;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.08);
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 1px;
    }

    svg {
      width: 15px;
      height: 15px;
    }
  }

  &__detail {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.06);
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    white-space: pre-line;

    p {
      margin: 3px 0;
    }
  }

  &--success {
    background: @success-soft;
    color: #14532d;
    border: 1px solid #bbf7d0;
  }

  &--error {
    background: @danger-soft;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  &--info {
    background: @info-soft;
    color: #1e40af;
    border: 1px solid #bfdbfe;
  }
}

// ==================== 表格 ====================
.table-wrap {
  overflow: auto;
  max-height: 430px;
  border: 1px solid @border;
  border-radius: @radius-sm;

  &--small {
    max-height: 230px;
  }
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 640px;

  th,
  td {
    padding: 11px 14px;
    text-align: left;
    border-bottom: 1px solid @border;
    vertical-align: middle;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: @text-secondary;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  tbody tr {
    transition: background-color 0.12s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child td {
      border-bottom: none;
    }
  }
}

.td-nowrap {
  white-space: nowrap;
}

.td-muted {
  color: @text-muted;
}

.td-mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
}

.td-truncate {
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-amount {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: @danger;
  white-space: nowrap;

  &--positive {
    color: @success;
  }
}

.empty-tip {
  text-align: center;
  padding: 28px 16px !important;
  color: @text-muted;
  font-size: 13px;
}

// ==================== 分页 ====================
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 16px;

  &__info {
    font-size: 13px;
    color: @text-secondary;
    font-variant-numeric: tabular-nums;
  }
}

// ==================== 账户管理 ====================
.account-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.account-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.balance-panel {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 16px;
  background: @primary-soft;
  border-radius: @radius-sm;

  &__label {
    font-size: 12px;
    color: @text-secondary;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: @primary;
    font-variant-numeric: tabular-nums;
  }
}

// ==================== 待处理业务 ====================
.task-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.task-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid @border;
  border-radius: @radius-sm;
  background: #fafbfe;
  transition: border-color 0.15s, background-color 0.15s;

  &__name {
    font-size: 13px;
    color: @text-secondary;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: @primary;
  }

  &--empty {
    opacity: 0.72;

    .task-chip__count {
      color: @text-muted;
    }
  }
}

// ==================== 备份控制 ====================
.backup-controls {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;

  .field--inline {
    flex: 1;
    min-width: 260px;
  }
}

// ==================== 清理过期文件 ====================
.cleanup-info {
  padding: 14px 16px;
  background: #fafbfe;
  border: 1px solid @border;
  border-radius: @radius-sm;
  font-size: 13px;
  color: @text-secondary;
  line-height: 1.7;
  margin-bottom: 14px;

  p {
    margin: 4px 0;
  }

  strong {
    color: @danger;
  }

  &__warn {
    color: #991b1b;
  }
}

// ==================== Toast ====================
.toast-container {
  position: fixed;
  top: 76px;
  right: 20px;
  z-index: 9997;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 240px;
  max-width: 340px;
  padding: 11px 12px 11px 14px;
  border-radius: @radius-sm;
  color: #fff;
  box-shadow: @shadow-lg;
  pointer-events: auto;

  .toast-icon {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
  }

  .toast-text {
    flex: 1;
    font-size: 13px;
    line-height: 1.5;
  }

  .toast-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.35);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 1px;
    }

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &--success { background: @success; }
  &--error   { background: @danger; }
  &--warning { background: @warning; }
  &--info    { background: @info; }
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

// ==================== 对话框 ====================
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9996;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: 100%;
  max-width: 420px;
  background: @surface;
  border-radius: 14px;
  padding: 24px;
  box-shadow: @shadow-lg;

  .modal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    margin-bottom: 14px;
    font-size: 22px;

    &--danger  { background: @danger-soft; color: @danger; }
    &--warning { background: @warning-soft; color: @warning; }
    &--info    { background: @info-soft; color: @info; }
  }

  .modal-title {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 600;
    color: @text;
  }

  .modal-message {
    margin: 0 0 16px;
    font-size: 14px;
    color: @text-secondary;
    line-height: 1.65;
    white-space: pre-line;
  }

  .modal-input-wrap {
    margin-bottom: 16px;
  }

  .modal-input-label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    color: @text-secondary;

    code {
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
      color: @danger;
      font-weight: 600;
    }
  }

  .modal-input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid @border-strong;
    border-radius: @radius-sm;
    font-size: 14px;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: @primary;
      box-shadow: 0 0 0 3px rgba(53, 85, 244, 0.16);
    }
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}
.modal-enter,
.modal-leave-to {
  opacity: 0;
}
.modal-enter .modal,
.modal-leave-to .modal {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

// ==================== 滚动条 ====================
.table-wrap::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}
.table-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 5px;

  &:hover {
    background: #94a3b8;
  }
}

// ==================== 响应式 ====================
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .task-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .form-grid {
    grid-template-columns: 1fr 1fr;

    .field--button {
      grid-column: span 2;
    }
  }
  .form-grid--refund {
    grid-template-columns: 1fr 1fr;

    .field--button {
      grid-column: span 2;
    }
  }
}

@media (max-width: 640px) {
  .admin-dashboard {
    padding: 0 0 40px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .card {
    padding: 18px 16px 20px;
  }

  .form-grid,
  .form-grid--refund {
    grid-template-columns: 1fr;

    .field--button {
      grid-column: auto;
    }
  }

  .order-info {
    grid-template-columns: 1fr;

    &__meta {
      padding-left: 0;
      border-left: none;
      border-top: 1px solid @border;
      padding-top: 12px;
    }
  }

  .task-grid {
    grid-template-columns: 1fr 1fr;
  }

  .filter-row,
  .account-row {
    align-items: stretch;

    .field--inline {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .account-row .btn,
  .filter-row .btn,
  .backup-controls .btn {
    width: 100%;
  }

  .toast-container {
    right: 12px;
    left: 12px;

    .toast-item {
      max-width: none;
    }
  }

  .sub-header,
  .card-header {
    align-items: flex-start;
  }
}

// ==================== 减少动态效果 ====================
@media (prefers-reduced-motion: reduce) {
  .btn,
  .input,
  .toast-item,
  .modal-overlay,
  .table tbody tr {
    transition: none !important;
  }
}
</style>
